// TUT-244 — o catálogo FECHADO de focos: o que um contador abre.
//
// No quadro do Canva (10/09) há quatro cartões cinzentos fora da moldura de
// qualquer tela, e a cliente comentou em CADA UM a mesma palavra: "POP UP".
// São os únicos comentários do quadro. As setas desenham uma cadeia de três
// saltos — contador → lista → ficha do processo → página do processo — e ela
// se repete em SEIS telas. Por isso a primitiva nasce antes delas.
//
// Este módulo é o de-para ÚNICO entre o contador e a lista que ele abre, no
// mesmo espírito de `etapas.ts` (o valor único que resumo e quadro leem) e de
// `indicadores.ts` (o de-para entre indicador e filtro). Se cada tela montasse
// o seu pop-up, um contador acabaria abrindo uma lista que conta outra coisa —
// e o número da origem discordaria do total do destino, que é pior do que
// número nenhum.
//
// **Nenhum método novo nasce nos adapters por causa deste módulo.** As três
// consultas — perícia, peça e laudo — são as MESMAS que o Agendamento já faz
// (`api.expertises`, `api.legalBriefsAcrossProcesses`,
// `api.expertReportsAcrossProcesses`). O foco declara o recorte; quem busca é
// a tela. Foi a lição que a TUT-243 pagou: somar endpoint que existe não
// esbarra na catraca do data-store.

import type { LinkProps } from "@tanstack/react-router";

import type { ContadorDoFinanceiro } from "./financeiro-quadros";

import type {
  RecorteDaMesa,
  ExpertReportQueueStatusKey,
  ExpertiseStatusKey,
  LegalBriefStatusName,
  LegalBriefTypeKey,
} from "./types";

/**
 * A consulta de um foco, por ORIGEM.
 *
 * União fechada sobre as cinco filas que já existem. Acrescentar origem aqui é
 * decisão de produto (uma tela nova da fila do Canva), não conveniência de
 * quem passa: cada origem custa um ramo em `PilhaDeFoco`.
 *
 * TUT-276 — `proposta` e `lancamento` entraram junto com os OITO contadores de
 * Clientes e do Financeiro. As duas reusam predicados que já existiam
 * (`propostasDoContador`, `lancamentosDoContador`) em vez de repetir o filtro
 * aqui: o `contador` só aponta qual recorte usar, quem sabe filtrar é o dono
 * do dado.
 */
export type ConsultaDoFoco =
  | {
      fonte: "pericia";
      status: readonly ExpertiseStatusKey[];
      /** TUT-309 — o recorte pelo assistente técnico (ver `ExpertiseListQuery`). */
      assistente?: "sem" | "com";
      /** TUT-526 — o roteiro: `pendente` é o "Pendente formulário"; `completo`, o "Aguardando perícia". */
      formulario?: "pendente" | "completo";
      /**
       * TUT-526 — "Pendente agendamento" soma às perícias "a agendar" o que
       * espera a ficha de agendamento: a solicitação aceita e o laudo nascido
       * do relatório. A lista conta o mesmo que o cartão.
       */
      fichasPendentes?: true;
      /**
       * TUT-527 — "Aguardando perícia" soma à agendada a perícia que aguarda a
       * data do juízo (a "Aguardando perícia" das linhas do Canva). A lista
       * conta o mesmo que o cartão.
       */
      aguardandoOJuizo?: true;
    }
  | {
      fonte: "peca";
      tipos?: readonly LegalBriefTypeKey[];
      status: readonly LegalBriefStatusName[];
      /**
       * TUT-530 — tira da lista o item já enviado ao advogado (o protocolo
       * espera o comprovante em "Aguardando protocolo"): a lista conta o mesmo
       * que o cartão.
       */
      semEnviadas?: true;
      /**
       * TUT-530 (revisão, 25/10) — soma à lista o laudo em AWAITING_FILING que
       * a M&H ainda NÃO enviou ao advogado: é trabalho por fazer, "Pendente
       * envio" ao lado da peça (o Canva separa "a M&H age" de "esperar o
       * advogado").
       */
      comLaudosAProtocolar?: true;
    }
  | { fonte: "laudo"; status: readonly ExpertReportQueueStatusKey[]; semEnviadas?: true }
  | {
      /**
       * TUT-530 (revisão, 25/10) — o envio ao advogado que JÁ foi e espera o
       * comprovante ("Aguardando protocolo" do Canva): o envio PROTOCOL SENT de
       * peça ou de laudo. O laudo sem envio não conta aqui.
       */
      fonte: "envio";
      aguardandoProtocolo: true;
    }
  | {
      fonte: "proposta";
      contador: "pendentes" | "assistencias" | "especiais" | "consultoria";
    }
  | {
      /**
       * TUT-313b — a Minha mesa da Visão geral detalhada. A lista vem de
       * `GET /dashboard/desk`, a MESMA conta que dá o número do cartão; a tela
       * não recorta nada.
       */
      fonte: "mesa";
      recorte: RecorteDaMesa;
    }
  | {
      fonte: "lancamento";
      contador:
        | "a-receber"
        | "a-pagar"
        | "pendente-documento"
        | "atrasados";
    };

/**
 * Os campos que uma linha de pop-up sabe mostrar — lista fechada, e por isso
 * a coluna não pode inventar acessor.
 *
 * `reclamada` e `detalhe` são NULÁVEIS de propósito: a perícia não carrega a
 * reclamada no domínio (`ExpertiseSummary` tem reclamante, processo e
 * especialidade, e mais nada), e pedi-la à API engordaria os dois adapters
 * pela catraca. Quem não tem o campo não declara a coluna — em vez de
 * declará-la e desenhar uma coluna inteira de traços.
 */
export type CampoDaLinha = "processo" | "reclamante" | "reclamada" | "detalhe";

export interface LinhaDoFoco {
  id: string;
  /**
   * Para onde o salto de nível 2 aponta quando o foco é `abre: "processo"` —
   * a ficha é sempre a do processo. Opcional desde a TUT-276: linha de foco
   * `abre: "tela"` não empilha ficha nenhuma, e por isso não carrega este
   * campo — ela carrega `destino`.
   */
  processId?: string;
  processo: string;
  reclamante: string;
  reclamada: string | null;
  detalhe: string | null;
  /**
   * TUT-276 — para onde o salto de nível 2 NAVEGA quando o foco é
   * `abre: "tela"`. `LinkProps` e não um par `{ to, search }` à mão: é o
   * MESMO tipo que `destino` já usa em `quadro-de-etapa.tsx`, e um segundo
   * formato aqui divergiria no primeiro `search` que alguém tipasse errado.
   */
  destino?: LinkProps;
  /**
   * TUT-556 — a linha É um envio ao advogado que espera algo dele ("Aguardando
   * protocolo"): a lista oferece "Cancelar envio" nela, o mesmo gesto da página
   * do processo (`useCancelarEnvio`), a quem tem a chave.
   */
  envioId?: string;
}

export interface ColunaDoFoco {
  campo: CampoDaLinha;
  titulo: string;
}

export type ChaveDeFoco =
  | "pendente-pericia"
  | "pendente-agendamento"
  | "pendente-assistente"
  | "pendente-formulario"
  | "pendente-relatorio"
  | "pendente-envio"
  | "pendente-protocolo"
  // TUT-246 — os quatro da Visão Geral. Ver o bloco em `FOCOS`.
  | "pericias"
  | "quesitos"
  | "laudos"
  | "impugnacoes"
  // TUT-276 — os quatro de Clientes e os quatro do Financeiro. Ver os dois
  // blocos em `FOCOS`.
  | "propostas-pendentes"
  | "assistencias"
  | "especiais"
  | "consultoria"
  | "a-receber"
  | "a-pagar"
  | "pendente-documento"
  | "atrasados"
  // TUT-313b — os quatro da Minha mesa (Visão geral detalhada).
  | "mesa-atribuidos"
  | "mesa-vencidos"
  | "mesa-vencem-hoje"
  | "mesa-revisao";

export interface DefinicaoDeFoco {
  chave: ChaveDeFoco;
  /** O título do pop-up. É o rótulo DELA, o mesmo do cartão que o abriu. */
  titulo: string;
  consulta: ConsultaDoFoco;
  colunas: readonly ColunaDoFoco[];
  /**
   * Qual foco cada linha abre.
   *
   * O campo existe declarado, e não implícito, porque é ele que impede a
   * cadeia de crescer sozinha: enquanto a folha for a ficha, a pilha tem dois
   * níveis e o terceiro NAVEGA. Se um dia uma linha abrir outra lista, isso
   * passa a ser uma decisão escrita aqui, revisável no diff, em vez de um
   * `if` escondido na tela.
   *
   * TUT-276 — `"tela"` é o segundo valor, para os oito contadores de Clientes
   * e do Financeiro: a cliente desenhou "todo contador abre a lista", mas só o
   * PROCESSO tem ficha — a proposta e o lançamento navegam direto para a tela
   * que já existe, sem nível 3.
   */
  abre: "processo" | "tela";
  /**
   * TUT-309 — o GESTO que a ficha já traz aberto. "Pendente assistente" abre o
   * processo com "Indicar assistente" em cima (decisão do dono, 25/09): a
   * lista de assistentes está ali, e resolver a pendência é um clique.
   */
  gestoNaFicha?: "indicar-assistente";
}

/** As colunas que a cliente desenhou no pop-up 1, na ordem em que ela as pôs. */
const colunasDoDesenho: readonly ColunaDoFoco[] = [
  { campo: "processo", titulo: "Processo" },
  { campo: "reclamante", titulo: "Reclamante" },
  { campo: "reclamada", titulo: "Reclamada" },
];

// No pop-up 1 do quadro os rótulos das colunas estão TROCADOS — `RECLAMADA`
// encabeça nomes de pessoa e `RECLAMANTE` encabeça nomes de empresa. Seguimos
// a semântica (pessoa é reclamante, empresa é reclamada/cliente), não o
// rótulo; está registrado em `docs/product/rascunho-cliente-10-09.md` como
// ponto a confirmar com ela.

/** A perícia troca a reclamada — que ela não tem — pela especialidade. */
const colunasDePericia: readonly ColunaDoFoco[] = [
  { campo: "processo", titulo: "Processo" },
  { campo: "reclamante", titulo: "Reclamante" },
  { campo: "detalhe", titulo: "Especialidade" },
];

/**
 * TUT-473 — o LAUDO mostra a reclamada E o cliente.
 *
 * Na TUT-472 a fila de laudos (`ExpertReportQueueItem`) só trazia o nome do
 * cliente, e `linhaDoLaudo` o punha sob o título "Reclamada": o mesmo
 * processo mostrava o cliente aqui e a reclamada de verdade em "Impugnações".
 * O conserto de então foi chamar a coluna de "Cliente"; agora a API manda a
 * reclamada (`defendant`), e a coluna "Reclamada" volta com ela. O cliente
 * continua ao lado, em `detalhe` — o campo que cada origem usa para a coluna
 * que só ela tem (a especialidade da perícia, o valor do lançamento).
 */
const colunasDoLaudo: readonly ColunaDoFoco[] = [
  { campo: "processo", titulo: "Processo" },
  { campo: "reclamante", titulo: "Reclamante" },
  { campo: "reclamada", titulo: "Reclamada" },
  { campo: "detalhe", titulo: "Cliente" },
];

/**
 * TUT-530 (revisão) — "Pendente envio" mistura a peça e o laudo que a M&H ainda
 * não enviou: a última coluna diz de QUE documento se trata.
 */
const colunasDoEnvioPendente: readonly ColunaDoFoco[] = [
  { campo: "processo", titulo: "Processo" },
  { campo: "reclamante", titulo: "Reclamante" },
  { campo: "reclamada", titulo: "Reclamada" },
  { campo: "detalhe", titulo: "Documento" },
];

/**
 * TUT-530 (revisão) — "Aguardando protocolo" lista o envio que espera o
 * comprovante: a última coluna diz qual documento foi e o prazo do advogado.
 */
const colunasDoProtocolo: readonly ColunaDoFoco[] = [
  { campo: "processo", titulo: "Processo" },
  { campo: "reclamante", titulo: "Reclamante" },
  { campo: "reclamada", titulo: "Reclamada" },
  { campo: "detalhe", titulo: "Documento" },
];

/**
 * TUT-276 — as colunas da PROPOSTA: nenhum campo novo, só título próprio
 * sobre os que já existem. `reclamada` guarda o cliente da proposta (a
 * organização, não uma pessoa); `detalhe` guarda "tipo de serviço · status",
 * o mesmo vocabulário que `ReportsPage`/`NewProposalMenu` já usam.
 */
const colunasDeProposta: readonly ColunaDoFoco[] = [
  { campo: "processo", titulo: "Proposta" },
  { campo: "reclamada", titulo: "Cliente" },
  { campo: "detalhe", titulo: "Situação" },
];

/**
 * TUT-276 — as colunas do LANÇAMENTO (despesa ou diligência): `reclamada`
 * troca de nome conforme quem pode aparecer na linha — só cliente em
 * "A receber", só representante em "A pagar", os dois quando o contador soma
 * as duas origens ("Pendente documento", "Atrasados").
 */
function colunasDeLancamento(tituloDoMeio: string): readonly ColunaDoFoco[] {
  return [
    { campo: "processo", titulo: "Processo" },
    { campo: "reclamada", titulo: tituloDoMeio },
    { campo: "detalhe", titulo: "Valor" },
  ];
}

/**
 * TUT-313b — as colunas da Minha mesa. `detalhe` diz o que é a linha ("Prazo ·
 * Manifestação · vence 26/09"), porque a lista mistura quatro origens.
 */
const colunasDaMesa: readonly ColunaDoFoco[] = [
  { campo: "processo", titulo: "Processo" },
  { campo: "detalhe", titulo: "O quê" },
  { campo: "reclamante", titulo: "Reclamante" },
  { campo: "reclamada", titulo: "Reclamada" },
];

/**
 * Os focos do Agendamento — o consumidor com que esta primitiva nasce.
 *
 * São as seis pendências de `pendenciasDoAgendamento` mais "Pendente formulário"
 * (coluna sem contador, TUT-530), e o recorte
 * de cada uma é o MESMO que a tela já buscava para o quadro. O que muda é o
 * gesto: a TUT-231 fez o cartão NAVEGAR para a lista filtrada; o desenho dela
 * diz pop-up. Ver o cabeçalho de `indicadores.ts`, que registra a re-decisão.
 *
 * "Pendente protocolo" é o caso que fecha o argumento: ele era o único cartão
 * SEM link, porque não existe lista de laudos endereçável por URL. Com o
 * pop-up ele deixa de ser exceção — a lista não precisa mais ser uma tela.
 */
export const FOCOS: Readonly<Record<ChaveDeFoco, DefinicaoDeFoco>> = {
  // TUT-309 — agendada E com assistente. A agendada sem assistente é o
  // "Pendente assistente", que vem antes no fluxo (agendamento → assistente
  // → perícia): as duas nunca contam a mesma perícia.
  // TUT-526 — e com o roteiro completo; sem ele, é o "Pendente formulário".
  "pendente-pericia": {
    chave: "pendente-pericia",
    titulo: "Aguardando perícia",
    consulta: { fonte: "pericia", status: ["scheduled"], assistente: "com", formulario: "completo", aguardandoOJuizo: true },
    colunas: colunasDePericia,
    abre: "processo",
  },
  "pendente-agendamento": {
    chave: "pendente-agendamento",
    titulo: "Pendente agendamento",
    consulta: { fonte: "pericia", status: ["to_schedule"], fichasPendentes: true },
    colunas: colunasDePericia,
    abre: "processo",
  },
  // TUT-309 — era a PEÇA "Indicação de assistente" em aberto; passou a ser a
  // perícia agendada SEM assistente (decisão do dono, 25/09): a pendência sai
  // quando alguém indica o assistente, e o gesto mora na própria ficha.
  "pendente-assistente": {
    chave: "pendente-assistente",
    titulo: "Pendente assistente",
    consulta: { fonte: "pericia", status: ["scheduled"], assistente: "sem" },
    colunas: colunasDePericia,
    abre: "processo",
    gestoNaFicha: "indicar-assistente",
  },
  // TUT-526 — a agendada com assistente cujo roteiro não passou da preparação
  // (M12): resolver é preparar o roteiro, que mora na ficha da perícia.
  "pendente-formulario": {
    chave: "pendente-formulario",
    titulo: "Pendente formulário",
    consulta: { fonte: "pericia", status: ["scheduled"], assistente: "com", formulario: "pendente" },
    colunas: colunasDePericia,
    abre: "processo",
  },
  "pendente-relatorio": {
    chave: "pendente-relatorio",
    titulo: "Aguardando relatório",
    consulta: { fonte: "pericia", status: ["pending_report"] },
    colunas: colunasDePericia,
    abre: "processo",
  },
  "pendente-envio": {
    chave: "pendente-envio",
    titulo: "Pendente envio",
    // Sem filtro de tipo, como em `listaDePecasPendentes`: `AWAITING_DELIVERY`
    // é o mesmo status que a Elaboração lê como "Finalizados", e o cartão
    // conta peça de qualquer tipo nesse estado.
    // TUT-530 (revisão) — e o laudo em AWAITING_FILING ainda sem envio.
    consulta: { fonte: "peca", status: ["AWAITING_DELIVERY"], semEnviadas: true, comLaudosAProtocolar: true },
    colunas: colunasDoEnvioPendente,
    abre: "processo",
  },
  // TUT-530 (revisão, 25/10) — "Pendente protocolo" virou "Aguardando
  // protocolo" do Canva: SÓ o que já foi enviado e espera o comprovante do
  // advogado (o envio de peça ou de laudo). A chave fica (links salvos). O
  // laudo sem envio é "Pendente envio".
  "pendente-protocolo": {
    chave: "pendente-protocolo",
    titulo: "Aguardando protocolo",
    consulta: { fonte: "envio", aguardandoProtocolo: true },
    colunas: colunasDoProtocolo,
    abre: "processo",
  },

  // -------------------------------------------------------------------------
  // TUT-246 — os QUATRO contadores do topo da Visão Geral, na ordem em que ela
  // os desenhou: `PERÍCIAS · QUESITOS · LAUDOS · IMPUGNAÇÕES`.
  //
  // **Eles contam o que está ABERTO, não o que já passou.** A Visão Geral é
  // tela de leitura — "será aberta para a empresa" —, e o que ela responde é
  // "quanto trabalho está de pé agora". Um contador que somasse o histórico
  // subiria para sempre e deixaria de significar qualquer coisa no segundo
  // mês; e o pop-up dele abriria uma lista que ninguém consegue esvaziar.
  //
  // O recorte de cada um é o MESMO que a tela da área já usa, e por isso o
  // número daqui bate com o de lá: perícia repete os três status abertos do
  // Agendamento, e as peças repetem o miolo da Elaboração
  // (`DRAFTING → IN_REVIEW → AWAITING_DELIVERY`).
  //
  // Nenhum endpoint novo: as três origens são as que a TUT-244 já declarou.
  // -------------------------------------------------------------------------
  pericias: {
    chave: "pericias",
    titulo: "Perícias",
    consulta: {
      fonte: "pericia",
      status: ["to_schedule", "scheduled", "pending_report"],
    },
    colunas: colunasDePericia,
    abre: "processo",
  },
  quesitos: {
    chave: "quesitos",
    titulo: "Quesitos",
    consulta: {
      fonte: "peca",
      tipos: ["QUESTIONS"],
      status: ["DRAFTING", "IN_REVIEW", "AWAITING_DELIVERY"],
    },
    colunas: colunasDoDesenho,
    abre: "processo",
  },
  laudos: {
    chave: "laudos",
    titulo: "Laudos",
    // `COMPLETED` é a CABEÇA da fila desde a TUT-242 — a perícia realizada que
    // ainda não tem laudo aberto. Ela conta como laudo a fazer, que é o que a
    // aba Laudos da Elaboração mostra na primeira coluna. `FILED` fica de
    // fora: protocolado é trabalho terminado.
    consulta: {
      fonte: "laudo",
      status: ["COMPLETED", "DRAFTING", "IN_REVIEW", "AWAITING_FILING"],
    },
    colunas: colunasDoLaudo,
    abre: "processo",
  },
  impugnacoes: {
    chave: "impugnacoes",
    titulo: "Impugnações",
    // Ela escreveu IMPUGNAÇÕES no desenho e disse "publicações" na fala
    // (01:47). O desenho manda — é a regra que já resolveu as colunas da
    // Elaboração —, e a pergunta 11 da fase 2 segue aberta para confirmar.
    consulta: {
      fonte: "peca",
      tipos: ["IMPUGNATION"],
      status: ["DRAFTING", "IN_REVIEW", "AWAITING_DELIVERY"],
    },
    colunas: colunasDoDesenho,
    abre: "processo",
  },

  // -------------------------------------------------------------------------
  // TUT-276 — os quatro contadores de CLIENTES: `PROPOSTAS PENDENTES ·
  // ASSISTÊNCIAS · ESPECIAIS · CONSULTORIA`. Decisão da cliente (20/09): "todo
  // contador que ela desenhou deve abrir a lista", e para estes quatro a linha
  // NAVEGA para a página da proposta (`abre: "tela"`) — só o processo tem
  // ficha.
  //
  // `contador` reusa exatamente o recorte de `propostasDoContador`, que é o
  // MESMO que `contadoresDePropostas` conta: o número do cartão e o total da
  // lista têm de concordar, ou o pop-up desmentiria o contador que o abriu.
  // -------------------------------------------------------------------------
  "propostas-pendentes": {
    chave: "propostas-pendentes",
    titulo: "Propostas pendentes",
    consulta: { fonte: "proposta", contador: "pendentes" },
    colunas: colunasDeProposta,
    abre: "tela",
  },
  assistencias: {
    chave: "assistencias",
    titulo: "Assistências",
    consulta: { fonte: "proposta", contador: "assistencias" },
    colunas: colunasDeProposta,
    abre: "tela",
  },
  especiais: {
    chave: "especiais",
    titulo: "Especiais",
    consulta: { fonte: "proposta", contador: "especiais" },
    colunas: colunasDeProposta,
    abre: "tela",
  },
  consultoria: {
    chave: "consultoria",
    titulo: "Consultoria",
    consulta: { fonte: "proposta", contador: "consultoria" },
    colunas: colunasDeProposta,
    abre: "tela",
  },

  // -------------------------------------------------------------------------
  // TUT-276 — os quatro contadores do FINANCEIRO: `A RECEBER · A PAGAR ·
  // PENDENTE DOCUMENTO · ATRASADOS`. Os dois primeiros abrem só a origem que
  // contam (diligência, despesa); os dois últimos somam as duas, como o
  // contador já soma — por isso a coluna do meio muda de rótulo.
  //
  // `contador` reusa `lancamentosDoContador`, o MESMO predicado que
  // `contadoresDoFinanceiro` usa para contar.
  // -------------------------------------------------------------------------
  "a-receber": {
    chave: "a-receber",
    titulo: "A receber",
    consulta: { fonte: "lancamento", contador: "a-receber" },
    colunas: colunasDeLancamento("Cliente"),
    abre: "tela",
  },
  "a-pagar": {
    chave: "a-pagar",
    titulo: "A pagar",
    consulta: { fonte: "lancamento", contador: "a-pagar" },
    colunas: colunasDeLancamento("Assistente externo"),
    abre: "tela",
  },
  "pendente-documento": {
    chave: "pendente-documento",
    titulo: "Pendente documento",
    consulta: { fonte: "lancamento", contador: "pendente-documento" },
    colunas: colunasDeLancamento("Cliente ou assistente externo"),
    abre: "tela",
  },
  atrasados: {
    chave: "atrasados",
    titulo: "Atrasados",
    consulta: { fonte: "lancamento", contador: "atrasados" },
    colunas: colunasDeLancamento("Cliente ou assistente externo"),
    abre: "tela",
  },
  // TUT-313b — a Minha mesa: prazo, peça, laudo e trabalho especial na mesma
  // lista, e por isso uma coluna diz O QUE é cada linha. A linha empilha a
  // ficha do processo; o trabalho especial sem processo navega para a tela de
  // trabalhos (a única linha sem ficha possível).
  "mesa-atribuidos": {
    chave: "mesa-atribuidos",
    titulo: "Atribuídos a mim",
    consulta: { fonte: "mesa", recorte: "atribuidos" },
    colunas: colunasDaMesa,
    abre: "processo",
  },
  "mesa-vencidos": {
    chave: "mesa-vencidos",
    titulo: "Vencidos",
    consulta: { fonte: "mesa", recorte: "vencidos" },
    colunas: colunasDaMesa,
    abre: "processo",
  },
  "mesa-vencem-hoje": {
    chave: "mesa-vencem-hoje",
    titulo: "Vencem hoje",
    consulta: { fonte: "mesa", recorte: "vencem-hoje" },
    colunas: colunasDaMesa,
    abre: "processo",
  },
  "mesa-revisao": {
    chave: "mesa-revisao",
    titulo: "Aguardam minha revisão",
    consulta: { fonte: "mesa", recorte: "revisao" },
    colunas: colunasDaMesa,
    abre: "processo",
  },
};

/**
 * O foco pedido pela URL, ou `undefined`.
 *
 * **A lista fechada se confere no PONTO DE LEITURA, não só na rota.** O
 * `validateSearch` descarta o que não é foco (há caso provando), mas o
 * `useSearch()` da rota filha chega mesclado com a busca do PAI, que não
 * valida nada — foi assim que `?mode=FOGUETE` deixou a tela de Viagens sem
 * chip nenhum aceso em 31/08 (TUT-204g). Aqui a consequência seria pior: um
 * `?foco=inventado` abriria um pop-up vazio e sem título.
 *
 * `Object.hasOwn` e não o acesso direto: o catálogo é um objeto literal, então
 * `FOCOS["constructor"]` devolveria a função herdada do `Object.prototype` —
 * um foco que existe no TypeScript como `DefinicaoDeFoco` e explode ao ler
 * `.titulo`. A URL é entrada do usuário; ela alcança o protótipo de graça.
 */
export function focoDaUrl(valor: unknown): DefinicaoDeFoco | undefined {
  if (typeof valor !== "string") return undefined;
  if (!Object.hasOwn(FOCOS, valor)) return undefined;
  return (FOCOS as Record<string, DefinicaoDeFoco>)[valor];
}

/** As chaves, para a rota validar sem repetir a lista à mão. */
export const CHAVES_DE_FOCO = Object.keys(FOCOS) as ChaveDeFoco[];

/**
 * Os quatro contadores do topo da Visão Geral, NA ORDEM DELA.
 *
 * A ordem é do desenho, não alfabética nem do catálogo: `PERÍCIAS · QUESITOS ·
 * LAUDOS · IMPUGNAÇÕES`. Ela mora aqui, e não na tela, para a tela não poder
 * inventar um quinto contador nem reordenar os quatro sem que o diff mostre.
 */
export const CHAVES_DA_VISAO_GERAL: readonly ChaveDeFoco[] = [
  "pericias",
  "quesitos",
  "laudos",
  "impugnacoes",
];

/**
 * Os quatro contadores das PROPOSTAS, na ordem do desenho dela (TUT-276).
 * Nasceram em Clientes; desde a TUT-343 moram em Propostas (a carteira saiu
 * de Clientes a pedido do dono, 28/09).
 *
 * Mora aqui, e não na tela, pelo mesmo motivo de `CHAVES_DA_VISAO_GERAL`: a
 * tela não pode inventar um quinto contador nem reordenar os quatro sem que o
 * diff mostre.
 */
export const CHAVES_DAS_PROPOSTAS: readonly ChaveDeFoco[] = [
  "propostas-pendentes",
  "assistencias",
  "especiais",
  "consultoria",
];

/** TUT-313b — os quatro da MINHA MESA, na ordem dos cartões da Detalhada. */
export const CHAVES_DA_MINHA_MESA: readonly ChaveDeFoco[] = [
  "mesa-atribuidos",
  "mesa-vencidos",
  "mesa-vencem-hoje",
  "mesa-revisao",
];

/** Os quatro contadores do FINANCEIRO, na ordem do desenho dela (TUT-276). */
export const CHAVES_DO_FINANCEIRO: readonly ContadorDoFinanceiro[] = [
  "a-receber",
  "a-pagar",
  "pendente-documento",
  "atrasados",
];
